import React, { type MouseEventHandler, type ReactNode } from 'react';

import classnames from 'classnames';

import styles from './Row.module.scss';

type RowProps = {
    /**
     * The content of the row.
     */
    children?: ReactNode;
    /**
     * The additional css class.
     */
    className?: string;
    /**
     * Test prop.
     */
    dataTestId?: string;
    /**
     * The gutter spacing.
     */
    gutter?: number;
    /**
     * A callback function that is triggered when the button is clicked.
     */
    onClick?: MouseEventHandler<HTMLElement>;
    /**
     * The role
     */
    role?: 'row';
    /**
     * The additional style.
     */
    style?: React.CSSProperties;
    /**
     * The wrapping condition.
     */
    wrap?: boolean;
};

export const Row = React.forwardRef<HTMLDivElement, RowProps>((props: RowProps, ref) => {
    const {
        children,
        className,
        dataTestId,
        gutter = 0,
        onClick,
        role,
        style,
        wrap = true,
        ...rest
    } = props;
    const rowStyle: React.CSSProperties = {};

    if (gutter) {
        rowStyle.marginLeft = gutter / 2;
        rowStyle.marginRight = gutter / 2;
    }

    return (
        <div
            className={classnames(styles.row, className, { [styles.noWrap]: wrap === false })}
            data-testid={dataTestId}
            onClick={onClick}
            ref={ref}
            role={role}
            style={{ ...rowStyle, ...style }}
            tabIndex={0}
            {...rest}
        >
            {children}
        </div>
    );
});

Row.displayName = 'Row';
